// Browser boot: terminal, filesystem, environment, then upstream pi's main().

import "../node/builtins.ts";
import { registerHostCommands } from "../bin/index.ts";
import { mkdirSync } from "../node/fs.ts";
import { configureProcess, DEFAULT_CWD, type Launch, rememberCwd, resolveLaunch } from "./env.ts";
import { captureStartupErrors, createStartupExtension, enterStage, filesystemConfigured, showStartupFailure } from "./failure.ts";
import { installLifecycle } from "./lifecycle.ts";
import { activateMount, listMounts, type MountState, mountNameOf, restoreMounts } from "./mounts.ts";
import { installNetworkRouter, relayStatus } from "./net.ts";
import { previewStatus } from "./preview.ts";
import { registerBrowserOAuthFlows } from "../oauth/providers.ts";
import { seedPackageAssets, seedSystem } from "./seed.ts";
import { acquireInstanceLock, createInstanceExtension } from "./single-tab.ts";
import { installAppearance } from "./appearance.ts";
import { createHostTerminal } from "./terminal.ts";
import { installTouchKeys, trackKeyboard } from "./touch-keys.ts";
import { configureVfs } from "./vfs.ts";
import { installNotices } from "../ui/notices.ts";
import { process, ProcessExit } from "../node/process.ts";

function setStatus(text: string | undefined): void {
	const status = document.getElementById("status");
	if (!status) return;
	status.textContent = text ?? "";
	status.hidden = text === undefined;
}

/** Show `text` with a button and resolve when it is clicked (a user gesture). */
function waitForClick(text: string, label: string): Promise<void> {
	setStatus(text);
	const button = document.createElement("button");
	button.className = "pi-btn pi-btn--primary";
	button.textContent = label;
	document.getElementById("status")?.append(button);
	button.focus();
	return new Promise((resolve) => button.addEventListener("click", () => resolve(), { once: true }));
}

/**
 * When the working directory is inside a mounted local folder, make sure the
 * folder is available. Browsers ask again for access after a restart, and that
 * request needs a click. Falls back to the default workspace otherwise.
 */
async function ensureCwdMounted(launch: Launch, states: Map<string, MountState>): Promise<string | undefined> {
	const name = mountNameOf(launch.cwd);
	if (name === undefined || states.get(name) === "mounted") return undefined;
	const record = (await listMounts().catch(() => [])).find((mount) => mount.name === name);
	if (record && states.get(name) === "needs-permission") {
		await waitForClick(`pi needs access to the local folder "${record.handle.name}" again.`, "Allow access");
		setStatus(`Mounting ${name}…`);
		if ((await activateMount(record, true)) === "mounted") return undefined;
	}
	launch.cwd = DEFAULT_CWD;
	return `Local folder /mnt/${name} is not available${record ? "" : " (it was unmounted)"}; using ${DEFAULT_CWD}.`;
}

async function boot(): Promise<void> {
	captureStartupErrors();
	const container = document.getElementById("terminal");
	if (!container) throw new Error("#terminal element missing");
	installAppearance();
	const host = createHostTerminal(container);
	trackKeyboard();
	installTouchKeys({ term: host.term, input: (data) => process.stdin.pushInput(data), filterInput: host.filterInput });
	installNotices();
	// For tests and debugging from devtools: the terminal buffer is the UI's state.
	(window as { __piWeb?: unknown }).__piWeb = { term: host.term };

	await acquireInstanceLock(() => setStatus(undefined));

	enterStage("Mounting filesystem"); setStatus("Mounting filesystem…");
	await configureVfs();
	filesystemConfigured();
	await installNetworkRouter();
	registerBrowserOAuthFlows();
	enterStage("Unpacking pi"); setStatus("Unpacking pi…");
	await seedPackageAssets();
	seedSystem(registerHostCommands());

	enterStage("Restoring folders");
	mkdirSync("/mnt", { recursive: true });
	const launch = resolveLaunch();
	const mountStates = await restoreMounts((name) => setStatus(`Mounting ${name}…`), { cwd: launch.cwd });
	const mountWarning = await ensureCwdMounted(launch, mountStates);
	mkdirSync(launch.cwd, { recursive: true });
	rememberCwd(launch.cwd);
	configureProcess(launch);
	installLifecycle(host);

	enterStage("Starting pi"); setStatus("Starting pi…");
	const [{ runPi }, faux, workspaces, backend, storage, environment, drop, transfer, gitAccess, sessionRestore, notifications, clone, app, preview, mcp, files, install, remote, appearance, onboarding, webHub] = await Promise.all([
		import("../entry/pi.ts"),
		import("../extensions/faux.ts"),
		import("../extensions/workspaces.ts"),
		import("../extensions/backend.ts"),
		import("../extensions/storage.ts"),
		import("../extensions/environment.ts"),
		import("../extensions/drop.ts"),
		import("../extensions/transfer.ts"),
		import("../extensions/git-access.ts"),
		import("../extensions/session-restore.ts"),
		import("../extensions/notifications.ts"),
		import("../extensions/clone.ts"),
		import("../extensions/app.ts"),
		import("../extensions/preview.ts"),
		import("../extensions/mcp.ts"),
		import("../extensions/files.ts"),
		import("../extensions/install.ts"),
		import("../extensions/remote.ts"),
		import("../extensions/appearance.ts"),
		import("../extensions/onboarding.ts"),
		import("../extensions/web-hub.ts"),
	]);
	await remote.prepareRemoteLaunch(launch);
	await sessionRestore.prepareSessionLaunch(launch);
	configureProcess(launch);
	backend.restoreBashBackend();
	const extensions = [
		workspaces.createWorkspaceExtension(), backend.createBackendExtension(),
		storage.createStorageExtension(), environment.createEnvironmentExtension({ network: relayStatus, preview: previewStatus }),
		transfer.createTransferExtension(), drop.createDropExtension({ importZip: transfer.importWorkspaceFile }),
		gitAccess.createGitAccessExtension(),
		sessionRestore.createSessionRestoreExtension(),
		notifications.createNotificationsExtension(),
		clone.createCloneExtension(),
		app.createAppExtension(),
		preview.createPreviewExtension(),
		mcp.createBrowserMcpExtension(),
		files.createFilesExtension(),
		install.createExtensionInstallExtension(),
		remote.createRemoteExtension(),
		appearance.createAppearanceExtension(),
		onboarding.createOnboardingExtension(),
		webHub.createWebHubExtension(),
		createInstanceExtension(),
		createStartupExtension(),
	];
	if (faux.fauxEnabled()) extensions.push(faux.createFauxExtension());
	setStatus(undefined);
	host.term.focus();
	if (mountWarning) host.term.write(`\x1b[33m${mountWarning}\x1b[0m\r\n`);
	await runPi(launch.args, extensions);
}

boot().catch((error: unknown) => {
	// pi called process.exit(); installLifecycle shows what follows.
	if (error instanceof ProcessExit) return;
	setStatus(undefined);
	showStartupFailure({ error });
	console.error(error);
});
